import { Box, MOBILE_MODAL_CONTENT_PORTAL, MobileModalSheet, Stack } from '@talos/kyoko';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MarketSelectorBlotter } from '../blotter/MarketSelectorBlotter';
import { useMarketSelectorContext } from '../MarketSelectorContext';
import { MobileMarketSelectorAggregationSelect } from './MobileMarketSelectorAggregationSelect';
import { MobileMarketSelectorBlotterActionItems } from './MobileMarketSelectorBlotterActionItems';

interface MobileMarketSelectorSheetProps {
  isOpen: boolean;
  onClose: () => void;
  panelTopPrefix?: ReactNode;
  panelBottomSuffix?: ReactNode;
  panelContentKey?: string;
  testID?: string;
}

export function MobileMarketSelectorSheet({
  isOpen,
  onClose,
  panelTopPrefix,
  panelBottomSuffix,
  panelContentKey,
  testID = 'mobile-market-selector-sheet-content',
}: MobileMarketSelectorSheetProps) {
  const { setIsOpen } = useMarketSelectorContext();
  const [hasBeenOpenedOnce, setHasBeenOpenedOnce] = useState(isOpen);

  useEffect(() => {
    setIsOpen(isOpen);
    setHasBeenOpenedOnce(current => current || isOpen);
  }, [isOpen, setIsOpen]);

  const sheet = (
    <MobileModalSheet isOpen={isOpen} onClose={onClose} title="Select markets" useSheetContent={false}>
      {hasBeenOpenedOnce ? (
        <Stack key={panelContentKey} gap="spacing12" data-testid={testID} flex="1" pt={72}>
          {panelTopPrefix ? (
            <Box flex="0 0 auto" borderBottom="1px solid" borderColor="backgroundDivider">
              {panelTopPrefix}
            </Box>
          ) : null}

          <Stack gap="spacing12" px="spacing16">
            <MobileMarketSelectorAggregationSelect />
            <MobileMarketSelectorBlotterActionItems />
          </Stack>

          <MarketSelectorBlotter h="100%" isMobile />

          {panelBottomSuffix ? (
            <Box flex="0 0 auto" borderTop="1px solid" borderColor="backgroundDivider">
              {panelBottomSuffix}
            </Box>
          ) : null}
        </Stack>
      ) : null}
    </MobileModalSheet>
  );

  if (document == null) {
    return sheet;
  }

  // Escape the parent sheet's stacking context while keeping the selector mounted in the same React tree.
  const portalTarget = document.getElementById(MOBILE_MODAL_CONTENT_PORTAL) ?? document.body;
  return createPortal(sheet, portalTarget);
}
